import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { RECORRIDO } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Footprints, Trash2, TriangleAlert as AlertTriangle } from "lucide-react";

export default function Recorridos() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const canDelete = ["admin", "supervisor"].includes(user?.role);

  const { data: recorridos = [], isLoading } = useQuery({
    queryKey: ["recorridos"],
    queryFn: () => api.get("/recorridos").then((r) => r.data),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/recorridos/${id}`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recorridos"] });
      qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
      toast.success("Recorrido eliminado");
    },
    onError: (e) => toast.error(apiError(e, "Error al eliminar")),
  });

  return (
    <div className="p-4 lg:p-8 max-w-7xl mx-auto">
      <header className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl lg:text-4xl font-extrabold tracking-tight text-navy">Recorridos diarios</h1>
          <p className="text-sm text-muted-foreground mt-1">Rondines de seguridad y novedades detectadas.</p>
        </div>
        <Link
          to="/recorridos/new"
          data-testid={RECORRIDO.newButton}
          className="flex items-center gap-2 h-11 px-4 bg-navy text-white font-semibold hover:bg-navy-hover"
        >
          <Plus size={18} /> Nuevo recorrido
        </Link>
      </header>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Cargando…</p>
      ) : recorridos.length === 0 ? (
        <div className="bg-white border border-border p-12 flex flex-col items-center text-center">
          <span className="w-14 h-14 bg-secondary text-navy flex items-center justify-center mb-4">
            <Footprints size={28} />
          </span>
          <h3 className="font-heading text-xl font-bold text-navy">Sin recorridos</h3>
          <p className="text-sm text-muted-foreground mt-1 mb-4 max-w-sm">Registra el primer recorrido de seguridad.</p>
          <Link
            to="/recorridos/new"
            data-testid={RECORRIDO.newButton}
            className="h-11 px-4 bg-navy text-white font-semibold hover:bg-navy-hover"
          >
            + Nuevo recorrido
          </Link>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {recorridos.map((r) => (
            <div
              key={r.id}
              data-testid={RECORRIDO.row}
              className="bg-white border border-border p-4 flex flex-col gap-2 hover:border-navy transition-colors cursor-pointer"
              onClick={() => navigate(`/recorridos/${r.id}`)}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-navy truncate">{r.titulo}</p>
                  <p className="text-xs text-muted-foreground truncate">{r.ubicacion || "Sin ubicación"}</p>
                </div>
                {r.estado === "con_novedad" ? (
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-critical/10 text-critical shrink-0">
                    <AlertTriangle size={12} /> Novedad
                  </span>
                ) : (
                  <span className="text-xs px-2 py-0.5 bg-success/10 text-success shrink-0">Sin novedad</span>
                )}
              </div>
              {r.novedad && <p className="text-xs text-muted-foreground line-clamp-2">{r.novedad}</p>}
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-border">
                <span className="text-xs text-muted-foreground">
                  {new Date(r.fecha || r.created_at).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" })}
                </span>
                {canDelete && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteMutation.mutate(r.id);
                    }}
                    data-testid={RECORRIDO.deleteButton}
                    className="text-critical hover:bg-critical/10 p-1.5"
                    aria-label="Eliminar"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
